/**
 * Service API pour les clients publics
 */

import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Instance axios pour les clients
const clientApi = axios.create({
  baseURL: `${API_URL}/client`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Intercepteur pour ajouter le token JWT
clientApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('clientAccessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Intercepteur pour gérer le refresh token
clientApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Si erreur 401 et on n'a pas déjà tenté de refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = localStorage.getItem('clientRefreshToken');

      if (refreshToken) {
        try {
          const response = await axios.post(`${API_URL}/client/auth/refresh`, {
            refresh_token: refreshToken,
          });

          const { accessToken } = response.data;
          localStorage.setItem('clientAccessToken', accessToken);

          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return clientApi(originalRequest);
        } catch (refreshError) {
          // Refresh token invalide, déconnecter l'utilisateur
          localStorage.removeItem('clientAccessToken');
          localStorage.removeItem('clientRefreshToken');
          window.location.href = '/client/login';
          return Promise.reject(refreshError);
        }
      }
    }

    return Promise.reject(error);
  }
);

// ========================================
// AUTHENTIFICATION
// ========================================

export interface RegisterAnefData {
  anef_identifiant: string;
  anef_password: string;
  account_password: string;
  langue?: string;
}

export interface LoginData {
  email: string;
  password: string;
}

export const clientAuthApi = {
  // Vérifier les identifiants ANEF (étape 1)
  verifyAnefCredentials: (anef_identifiant: string, anef_password: string) =>
    axios.post(`${API_URL}/client/auth/verify-anef`, { anef_identifiant, anef_password }),

  // Inscription via ANEF
  registerViaAnef: (data: RegisterAnefData) =>
    axios.post(`${API_URL}/client/auth/register-anef`, data),

  // Connexion
  login: (data: LoginData) =>
    axios.post(`${API_URL}/client/auth/login`, data),

  // Rafraîchir le token
  refresh: (refreshToken: string) =>
    axios.post(`${API_URL}/client/auth/refresh`, { refresh_token: refreshToken }),

  // Déconnexion
  logout: (refreshToken: string) =>
    axios.post(`${API_URL}/client/auth/logout`, { refresh_token: refreshToken }),

  // Vérifier l'email
  verifyEmail: (token: string) =>
    axios.get(`${API_URL}/client/auth/verify-email/${token}`),

  // Mot de passe oublié
  forgotPassword: (email: string) =>
    axios.post(`${API_URL}/client/auth/forgot-password`, { email }),

  // Réinitialiser le mot de passe
  resetPassword: (token: string, newPassword: string) =>
    axios.post(`${API_URL}/client/auth/reset-password`, {
      token,
      new_password: newPassword,
    }),

  // Récupérer le profil
  getProfile: () => clientApi.get('/auth/me'),

  // Reconnecter les identifiants ANEF
  reconnectAnef: (anef_password: string) =>
    clientApi.post('/auth/reconnect-anef', { anef_password }),

  // Changer le mot de passe
  changePassword: (current_password: string, new_password: string) =>
    clientApi.post('/auth/change-password', { current_password, new_password }),

  // Supprimer le compte
  deleteAccount: (password: string) =>
    clientApi.delete('/auth/delete-account', { data: { password } }),
};

// ========================================
// DOSSIERS ANEF
// ========================================

export const clientDossiersApi = {
  // Liste des dossiers
  getAll: () => clientApi.get('/dossiers'),

  // Détail d'un dossier
  getById: (id: number) => clientApi.get(`/dossiers/${id}`),

  // Événements d'un dossier
  getEvents: (dossierId: number) => clientApi.get(`/dossiers/${dossierId}/events`),

  // Marquer les événements comme lus
  markEventsAsRead: (dossierId: number) =>
    clientApi.post(`/dossiers/${dossierId}/events/mark-read`),

  // Marquer un événement spécifique comme lu
  markEventAsRead: (dossierId: number, eventId: number) =>
    clientApi.post(`/dossiers/${dossierId}/events/${eventId}/mark-read`),

  // Synchroniser TOUS les dossiers ANEF
  syncAll: () => clientApi.post('/dossiers/sync'),

  // Forcer une synchronisation d'un dossier spécifique
  syncDossier: (dossierId: number) =>
    clientApi.post(`/dossiers/${dossierId}/sync`),

  // Mettre à jour les paramètres de synchronisation
  updateSyncSettings: (dossierId: number, settings: { sync_enabled?: boolean; notify_on_update?: boolean }) =>
    clientApi.put(`/dossiers/${dossierId}/sync-settings`, settings),

  // Statistiques des dossiers
  getStats: () => clientApi.get('/dossiers/stats/summary'),
};

// ========================================
// COMPTES ANEF RATTACHÉS
// ========================================

export const linkedAnefAccountsApi = {
  // Liste des comptes rattachés
  getAll: () => clientApi.get('/linked-anef-accounts'),

  // Ajouter un compte rattaché
  create: (data: {
    anef_identifiant: string;
    anef_password: string;
    display_name?: string;
    relation?: string;
  }) => clientApi.post('/linked-anef-accounts', data),

  // Mettre à jour un compte rattaché
  update: (id: number, data: {
    anef_password?: string;
    display_name?: string;
    relation?: string;
    sync_enabled?: boolean;
  }) => clientApi.put(`/linked-anef-accounts/${id}`, data),

  // Détacher un compte
  delete: (id: number) => clientApi.delete(`/linked-anef-accounts/${id}`),

  // Synchroniser un compte
  sync: (id: number) => clientApi.post(`/linked-anef-accounts/${id}/sync`),
};

// ========================================
// ABONNEMENT
// ========================================

export const clientSubscriptionApi = {
  // Récupérer l'abonnement actuel
  getCurrent: () => clientApi.get('/subscription'),

  // Créer une session Stripe Checkout
  createCheckoutSession: () => clientApi.post('/subscription/checkout'),

  // Obtenir l'URL du portail de facturation
  getBillingPortalUrl: () => clientApi.get('/subscription/billing-portal'),

  // Alias pour compatibilité
  getBillingPortal: () => clientApi.get('/subscription/billing-portal'),

  // Annuler l'abonnement
  cancel: () => clientApi.post('/subscription/cancel'),

  // Réactiver l'abonnement
  reactivate: () => clientApi.post('/subscription/reactivate'),

  // Historique des paiements
  getPayments: () => clientApi.get('/subscription/payments'),
};

// ========================================
// NOTIFICATIONS PUSH
// ========================================

export const clientPushApi = {
  // S'abonner aux notifications push
  subscribe: (subscription: PushSubscription) =>
    clientApi.post('/push/subscribe', { subscription }),

  // Se désabonner
  unsubscribe: () => clientApi.post('/push/unsubscribe'),

  // Liste des devices abonnés
  getDevices: () => clientApi.get('/push/devices'),

  // Supprimer un device
  removeDevice: (deviceId: number) =>
    clientApi.delete(`/push/devices/${deviceId}`),
};

// ========================================
// STATISTIQUES
// ========================================

export const clientStatsApi = {
  // Statistiques d'utilisation
  getUsageStats: () => clientApi.get('/stats/usage'),

  // Enregistrer une vue
  trackView: (type: string, itemId: number) =>
    clientApi.post('/stats/track', { type, item_id: itemId }),
};

// ========================================
// TYPES
// ========================================

export interface ClientUser {
  id: number;
  anef_identifiant?: string;
  nom: string;
  prenom: string;
  email: string;
  telephone?: string;
  date_naissance?: string;
  nationalite?: string;
  langue?: string;
  status?: string;
  email_verified?: boolean;
  source_type?: string;
  managed_client_id?: number;
  // Propriétés pour les cybers connectés via PWA
  username?: string;
  role?: 'admin' | 'cyber';
  user_type?: 'cyber' | 'client';
  cyber_manager?: {
    id: number;
    nom: string;
    prenom: string;
    email: string;
    telephone?: string;
    relation: {
      id: number;
      status: string;
      suivi_active: boolean;
      partage_dossiers: boolean;
      partage_notifications: boolean;
    };
  };
  subscription: {
    id: number;
    status: string;
    is_trial: boolean;
    trial_ends_at?: string;
    current_period_end: string;
  };
}

export interface Dossier {
  id: number;
  client_account_id: number;
  numero_demande?: string;
  type_demande?: string;
  anef_statut_actuel?: string;
  anef_date_depot?: string;
  anef_derniere_mise_a_jour?: string;
  anef_prefecture?: string;
  anef_commentaires?: string;
  anef_details_complets?: any;
  derniere_notification?: string;
  date_derniere_notification?: string;
  last_sync_at?: string;
  last_synced_at?: string;
  sync_enabled: boolean;
  notify_on_update?: boolean;
  is_active: boolean;
  unread_events_count?: number;
  client_nom?: string;
  client_prenom?: string;
  client_email?: string;
  client_telephone?: string;
  client_langue?: string;
  created_at: string;
  updated_at: string;

  // Nouvelles données enrichies
  anef_id_demande?: string;
  anef_notifications?: any[];
  anef_dernier_titre?: string;
  anef_dernier_titre_date?: string;
  anef_demandes_complement?: any[];
  anef_has_complement?: boolean | number;
  anef_historique_titres?: any[];

  // Champs pour dossiers cyber-managed
  titre?: string;
  statut?: string;
  description?: string;
  date_depot?: string;
  anef_numero_demande?: string;
  anef_data_raw?: any;
  commentaires?: string;
  anef_derniere_sync?: string;
  monitoring_actif?: boolean;
  source_type?: 'anef' | 'linked' | 'cyber_managed';
  derniere_maj?: string;

  // Champs pour dossiers rattachés (linked)
  linked_display_name?: string;
  linked_relation?: string;
  linked_anef_identifiant?: string;
}

export interface DossierEvent {
  id: number;
  client_dossier_id: number;
  event_type: 'status_change' | 'document_requested' | 'document_validated' |
                'document_rejected' | 'appointment_scheduled' | 'notification_received' |
                'complement_requested' | 'decision_made' | 'other';
  title: string;
  description?: string;
  old_value?: string;
  new_value?: string;
  metadata?: any;
  notification_sent: boolean;
  notification_read: boolean;
  event_date: string;
  created_at: string;
}

export interface Subscription {
  id: number;
  client_account_id: number;
  plan_type: 'trial' | 'monthly' | 'yearly';
  is_trial: boolean;
  trial_ends_at?: string;
  status: 'active' | 'cancelled' | 'expired' | 'payment_failed' | 'pending';
  current_period_start: string;
  current_period_end: string;
  price_amount: number;
  currency: string;
  payment_method?: string;
  next_billing_date?: string;
  cancel_at_period_end: boolean;
  created_at: string;
  updated_at: string;
}

export interface Payment {
  id: number;
  amount: number;
  currency: string;
  status: 'pending' | 'succeeded' | 'failed' | 'refunded' | 'cancelled';
  provider: string;
  invoice_url?: string;
  receipt_url?: string;
  paid_at?: string;
  created_at: string;
}

export default clientApi;
